/* @flow */

import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import memoize from 'lodash.memoize';

import { getLabel } from '../utils/i18n';
import FlexBox from './FlexBox';
import { FiatPrice } from './FiatPrice';
import {
  decimalPlaces, formatNumber, formatNumberWithK, pencentage,
} from '../utils/number';
import feature from '../config/feature';

// for performance, avoids creating a RegExp each call
const getPriceMatcherRegexp = memoize(decimals => new RegExp(`[0-9]*\\.[0-9]{${decimals}}`));

const { enableVolumnWithK } = feature;

const _formatNumber = enableVolumnWithK ? formatNumberWithK : formatNumber;

const Wrapper = styled(FlexBox)`
  margin-top: 1px;
`;
const TickerBox = styled(FlexBox)`
  flex: 1 1 auto;
  justify-content: center;
  white-space: nowrap;
`;
const TextLabel = styled(FlexBox)`
  color: ${({ theme }) => theme.fontColor2};
`;
const Amount = styled.span`
  white-space: nowrap;
  font-weight: ${({ fontWeight }) => fontWeight || 'bold'};
  font-size: 14px;
  color: ${({ theme }) => theme.fontColor};
`;
const GreenAmount = styled(Amount)` color: ${({ theme }) => theme.up}; `;
const RedAmount = styled(Amount)` color: ${({ theme }) => theme.down}; `;
const WhiteAmount = styled(Amount)` color: ${({ theme }) => theme.fontColor}; `;
const StyledFiatPrice = styled.span`
  color: ${({ theme }) => theme.fiatPriceColor};
  font-size: ${({ theme }) => theme.fontSizeSm};
  margin-left: 5px;
`;

const showPrice = (current: string, last, decimals, withFiatPrice = false, extra = null) => {
  const matcher = getPriceMatcherRegexp(decimals);
  let display = current.match(matcher);
  if (!display) display = [current];
  const formatPrice = (p: string) => formatNumber(p, decimals);
  const currentNumber = parseFloat(current);
  last = parseFloat(last);
  let ColorfulAmount = Amount;
  if (currentNumber > last) {
    ColorfulAmount = GreenAmount;
  }
  if (currentNumber < last) {
    ColorfulAmount = RedAmount;
  }
  if (currentNumber === last) {
    ColorfulAmount = WhiteAmount;
  }
  return (
    <div>
      <ColorfulAmount>{currentNumber < 0 ? '-' : ''}{formatPrice(display[0])}</ColorfulAmount>
      {withFiatPrice ? <Amount>{extra}</Amount> : <ColorfulAmount>{extra}</ColorfulAmount>}
    </div>);
};

const isLoaded = (props) => props.ticker && Array.isArray(props.trade) && props.trade.length > 1;

const displayIfLoaded = (props: Object, displayFn: Function, WrapComponent = 'div') => isLoaded(props) ? displayFn(props) : <WrapComponent>— </WrapComponent>;

const getTickSize = ticker => {
  try {
    return decimalPlaces(ticker.tickSize.toString());
  } catch (err) {
    return 8;
  }
};

const getLastPrice = (trade) => {
  if (trade.length > 1) {
    return trade[1].p;
  }
  return trade[0] && trade[0].p;
};

export default connect(
  (
    {
      tickers,
      trades,
      fiatRate,
      cnyUsdRate,
      locale,
      currencyList,
      userPreferredCurrency,
    },
    { symbol, i18n },
  ) => ({
    ticker: tickers[symbol],
    decimals: getTickSize(tickers[symbol]),
    trade: trades[symbol],
    fiatRate,
    cnyUsdRate,
    locale,
    i18n,
    currencyList,
    preferredCurrency: userPreferredCurrency,
  })
)(props => (
  <Wrapper {...props}>
    <TickerBox flex="0" direction="column" wrap="nowrap">
      <TextLabel>{getLabel('exchange-trade-lastPrice', props.i18n)}</TextLabel>
      {
        displayIfLoaded(props, () => showPrice(
          props.trade[0].p,
          getLastPrice(props.trade),
          props.decimals,
          true,
          <Fragment>
            <StyledFiatPrice>
              <FiatPrice
                preferredCurrency={props.preferredCurrency}
                locale={props.locale}
                currencyList={props.currencyList}
                fiatRate={props.fiatRate}
                quoteAsset={props.ticker.qa}
                price={props.ticker.c}
              />
            </StyledFiatPrice>
          </Fragment>
        ),
        Amount,
        )
      }
    </TickerBox>
    <TickerBox flex="0" direction="column" wrap="nowrap">
      <TextLabel>{getLabel('exchange-trade-24hChange', props.i18n)}</TextLabel>
      {displayIfLoaded(props, () => showPrice(
        (parseFloat(props.trade[0].p) - parseFloat(props.ticker.o)).toFixed(props.decimals),
        0,
        props.decimals,
        false,
        <Fragment>
          &nbsp;&nbsp;
          {pencentage(parseFloat(props.trade[0].p) - parseFloat(props.ticker.o),
            parseFloat(props.ticker.o), {
              withSymbol: true,
            })}
        </Fragment>),
      Amount)}
    </TickerBox>
    <TickerBox flex="0" direction="column" wrap="nowrap">
      <TextLabel>{getLabel('exchange-trade-24hHigh', props.i18n)}</TextLabel>
      <Amount>
        {displayIfLoaded(props, () => formatNumber(props.ticker.h, props.decimals))}
      </Amount>
    </TickerBox>
    <TickerBox flex="0" direction="column" wrap="nowrap">
      <TextLabel>{getLabel('exchange-trade-24hLow', props.i18n)}</TextLabel>
      <Amount>
        {displayIfLoaded(props, () => formatNumber(props.ticker.l, props.decimals))}
      </Amount>
    </TickerBox>
    <TickerBox flex="0" direction="column" wrap="nowrap">
      <TextLabel>{getLabel('exchange-trade-24hVolume', props.i18n)}</TextLabel>
      <Amount>
        {
          displayIfLoaded(props, () => _formatNumber(
            props.ticker.q ? props.ticker.q : props.ticker.v * props.trade[0].p, 2
          ))
        }
        {isLoaded(props) ? ` ${props.quoteSymbol}` : null}
      </Amount>
    </TickerBox>
  </Wrapper>
));
